import isNil from 'lodash/isNil'

import type { Company } from '@/api/types/hor'
import type { CurrentUserResponse } from '@/api/types/user'
import {
  PWC_RMC_ADMIN_COMPANIES,
  PWC_RMC_BOOKER_COMPANIES,
  PWC_RMC_USER_COMPANIES,
} from '@/constants/pwc'
import { rmcRoles } from '@/constants/roles'
import { valueLabelOption } from '@/lib/helpers'

import { LOAD_ITEMS_LIMIT } from './constants'

import type { BaseOptionType } from '../Select'
import type { LoadOptionsRequest } from '../SelectAsync'
import type { CorporateRequestType, CorporateRequestParamsType } from './types'

export const convertCompanyToOption = (company: Company) =>
  valueLabelOption(company.id, company.companyName)

export const requestWrapper =
  (
    request: CorporateRequestType,
    corporateRequestParams?: CorporateRequestParamsType
  ): LoadOptionsRequest<BaseOptionType<number>, undefined> =>
  async (params) => {
    const response = await request({
      offset: params.offset,
      limit: LOAD_ITEMS_LIMIT,
      ...(params.search && { text: params.search }),
      ...corporateRequestParams,
    })

    const hasMore = response?.data.total > params.offset + LOAD_ITEMS_LIMIT
    const options = response?.data.items.map(convertCompanyToOption)
    return {
      hasMore,
      options,
    }
  }

export const getAllowedPwcCompanies = (user: CurrentUserResponse) => {
  const isRmc = user?.rmc
  const isRmcAdmin = isRmc && user?.rmcRole === rmcRoles.admin.value
  const isRmcUser = isRmc && user?.rmcRole === rmcRoles.user.value
  const isRmcBooker = isRmc && user?.rmcRole === rmcRoles.booker.value
  if (isRmcUser) {
    return PWC_RMC_USER_COMPANIES
  }

  if (isRmcBooker) {
    return PWC_RMC_BOOKER_COMPANIES
  }

  if (isRmcAdmin) {
    return PWC_RMC_ADMIN_COMPANIES
  }

  return []
}

export const getCompanyBySlug = (companies: Company[], slug: string) =>
  companies.find((company) => company.slug === slug)

export const getPreselectedProps = (
  companies: Company[],
  request: LoadOptionsRequest<BaseOptionType<number>, undefined>,
  isUninitialized: boolean,
  slug: string | null
) => {
  if (isNil(slug)) {
    return {
      isDisabled: false,
      value: undefined,
    }
  }

  if (isUninitialized) {
    request({ offset: 0, search: undefined })
  }

  const company = getCompanyBySlug(companies, slug)

  return {
    isDisabled: !!company,
    value: company ? convertCompanyToOption(company) : undefined,
  }
}
